import { auth, firebaseConfig } from '../config/firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  updateProfile,
  User
} from 'firebase/auth';
import { deleteApp, initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

export interface FirebaseUserData {
  email: string;
  password: string;
  displayName: string;
  role: 'customer' | 'admin';
  permissions: string[];
  customerId?: string;
}

export interface FirebaseUserResult {
  uid: string;
  email: string;
  displayName: string;
  customClaims?: {
    role: string;
    permissions: string[];
    customerId?: string;
  };
}

class FirebaseAdminService {
  
  /**
   * Creates a new Firebase user with role-based custom claims
   * Note: Custom claims require Firebase Admin SDK on the backend
   * For now, we'll store role info in the user's display name and use a backend service
   */
  async createFirebaseUser(userData: FirebaseUserData): Promise<FirebaseUserResult> {
    try {
      // IMPORTANT:
      // We must NOT create users with the primary auth instance, because that would
      // sign the admin out and sign in as the newly created user.
      // Use a secondary Firebase app + auth instance instead.
      const secondaryApp = initializeApp(firebaseConfig, `secondary-auth-${Date.now()}`);
      const secondaryAuth = getAuth(secondaryApp);

      // Create user with email and password (secondary auth session)
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        userData.email,
        userData.password
      );

      const user = userCredential.user;

      // Update user profile with display name and role info
      await updateProfile(user, {
        displayName: `${userData.displayName}|${userData.role}|${userData.customerId || ''}`
      });

      // In a real implementation, you would call your backend to set custom claims
      // Example: await this.setCustomClaims(user.uid, userData.role, userData.permissions, userData.customerId);

      // Cleanup secondary auth session/app
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      return {
        uid: user.uid,
        email: user.email!,
        displayName: userData.displayName,
        customClaims: {
          role: userData.role,
          permissions: userData.permissions,
          customerId: userData.customerId
        }
      };
    } catch (error: any) {
      console.error('Error creating Firebase user:', error);
      throw new Error(`Firebase user creation failed: ${error.message}`);
    }
  }

  /**
   * Generates a temporary password for new users
   * In production, you might want to send a password reset email instead
   */
  generateTemporaryPassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    
    // Ensure password meets Firebase requirements
    password += 'A'; // Uppercase
    password += 'a'; // Lowercase  
    password += '1'; // Number
    password += '!'; // Special char
    
    // Add random characters to reach 12 characters
    for (let i = 4; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // Shuffle the password
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  }

  /**
   * Updates user role and permissions
   * This would typically call your backend to update custom claims
   */
  async updateUserRole(uid: string, role: string, permissions: string[], customerId?: string): Promise<void> {
    try {
      // In a real implementation, call your backend API to update custom claims
      // Example: await axios.post('/api/admin/update-user-claims', { uid, role, permissions, customerId });
      
      console.log(`Would update user ${uid} with role ${role} and permissions:`, permissions);
      
      // For now, we can update the display name to reflect the new role
      const user = auth.currentUser;
      if (user && user.uid === uid) {
        const currentDisplayName = user.displayName?.split('|')[0] || 'User';
        await updateProfile(user, {
          displayName: `${currentDisplayName}|${role}|${customerId || ''}`
        });
      }
    } catch (error: any) {
      console.error('Error updating user role:', error);
      throw new Error(`Failed to update user role: ${error.message}`);
    }
  }

  /**
   * Deletes a Firebase user
   * This requires Firebase Admin SDK on the backend
   */
  async deleteFirebaseUser(uid: string): Promise<void> {
    try {
      // In a real implementation, call your backend to delete the user
      // Example: await axios.delete(`/api/admin/users/${uid}`);
      
      console.log(`Would delete Firebase user ${uid}`);
      
      // Note: Deleting users requires Admin SDK on the server side
      // The client SDK cannot delete users other than the currently authenticated user
    } catch (error: any) {
      console.error('Error deleting Firebase user:', error);
      throw new Error(`Failed to delete Firebase user: ${error.message}`);
    }
  }

  /**
   * Gets user information including custom claims
   * This would typically call your backend to get the full user data
   */
  async getUserWithClaims(uid: string): Promise<FirebaseUserResult | null> {
    try {
      // In a real implementation, call your backend to get user with claims
      // Example: const response = await axios.get(`/api/admin/users/${uid}`);
      
      const user = auth.currentUser;
      if (user && user.uid === uid) {
        const displayNameParts = user.displayName?.split('|') || [];
        return {
          uid: user.uid,
          email: user.email!,
          displayName: displayNameParts[0] || user.displayName || 'User',
          customClaims: {
            role: displayNameParts[1] || 'customer',
            permissions: [], // Would be fetched from backend
            customerId: displayNameParts[2] || undefined
          }
        };
      }
      
      return null;
    } catch (error: any) {
      console.error('Error getting user with claims:', error);
      throw new Error(`Failed to get user data: ${error.message}`);
    }
  }

  /**
   * Sends password reset email to user
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      // Import sendPasswordResetEmail when needed
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      throw new Error(`Failed to send password reset email: ${error.message}`);
    }
  }

  /**
   * Verifies if current user has required permissions
   */
  async verifyPermissions(requiredPermissions: string[]): Promise<boolean> {
    try {
      const user = auth.currentUser;
      if (!user) return false;

      // In a real implementation, get claims from backend
      const userClaims = await this.getUserWithClaims(user.uid);
      if (!userClaims?.customClaims) return false;

      return requiredPermissions.every(permission => 
        userClaims.customClaims!.permissions.includes(permission)
      );
    } catch (error) {
      console.error('Error verifying permissions:', error);
      return false;
    }
  }
}

export const firebaseAdminService = new FirebaseAdminService(); 